import { Commodity } from "@/pages/sy/models/Commodity";
import { TextField, Typography, Box } from "@mui/material";
import { Autocomplete } from "@mui/material";

interface CommoditySelectorForOperationProps {
  commodities: Commodity[];
  value: number | undefined | null;
  onChange: (commodityId: number | null) => void;
  disabled: boolean;
  bgColor: string;
  borderColor: string;
}

export const CommoditySelectorForOperation = ({
  commodities,
  value,
  onChange,
  disabled,
  bgColor,
  borderColor,
}: CommoditySelectorForOperationProps) => {
  const selectedCommodity = value
    ? commodities.find((c) => c.CommodityId === value)
    : null;

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Commodity,
  ) => (
    <li {...props}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="span"
          sx={{
            width: "24px",
            height: "24px",
            borderRadius: "4px",
            display: "inline-block",
            mr: "10px",
            bgcolor: (theme) =>
              option.CommodityColor || theme.palette.grey[600],
          }}
        />
        <Typography variant="h6">{option.CommodityName}</Typography>
      </Box>
    </li>
  );

  return (
    <>
      <Typography sx={{ fontSize: "1.6rem", fontWeight: 600, mb: 1.5 }}>
        Commodity
      </Typography>
      <Autocomplete
        options={commodities}
        getOptionLabel={(option) => {
          if (typeof option === "object" && option !== null) {
            return option.CommodityName || "";
          }
          return "";
        }}
        value={selectedCommodity}
        onChange={(_, newValue) => {
          onChange(newValue ? newValue.CommodityId : null);
        }}
        renderOption={renderOption}
        disabled={disabled}
        sx={{
          "& .MuiAutocomplete-inputRoot": {
            padding: "16px 20px",
          },
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: "400px",
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            },
          },
          listbox: {
            sx: {
              "& .MuiAutocomplete-option": {
                minHeight: "80px",
                display: "flex",
                alignItems: "center",
                fontSize: "1.8rem",
                padding: "20px 24px",
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="medium"
            fullWidth
            placeholder={!selectedCommodity ? "Select a commodity..." : ""}
            required
            slotProps={{
              input: {
                ...params.InputProps,
                sx: {
                  bgcolor: disabled ? bgColor : "white",
                  borderColor: borderColor,
                  minHeight: "70px",
                  fontSize: "1.8rem",
                  padding: "20px",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "2px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "3px",
                  },
                },
              },
              htmlInput: {
                ...params.inputProps,
                style: {
                  fontSize: "1.8rem",
                  height: "30px",
                },
              },
            }}
          />
        )}
      />
    </>
  );
};
